<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <!-- Register v1 -->
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <ok-logo />
        </b-link>
        <error-alert
          v-if="$route.hash === '#redirected'"
          :error="$t('auth.please-register-to-continue')"
          variant="warning"
        />
        <error-alert
          v-if="error"
          :error="error"
        />

        <b-alert
          v-if="message"
          :show="true"
          variant="success"
          :dismissible="false"
        >
          <div class="p-2">
            <div class="font-weight-bold">
              {{ $t('auth.register-request-title',[name]) }}
            </div>
            <div
              class="font-weight-semi-bold mt-2"
            >{{ $t('auth.register-request-msg') }}</div>
          </div>
        </b-alert>
        <template v-else>
          <b-card-title class="mb-1 text-center">
            {{ $t('auth.register-hint') }}
          </b-card-title>
          <b-card-text class="mb-2 text-center">
            {{ $t('auth.no-credit') }}
          </b-card-text>
          <!-- form -->
          <validation-observer ref="registerForm">
            <b-form class="auth-register-form mt-2">
              <ok-text-input
                v-model="name"
                :label="$t('fields.full-name')"
                :is-required="true"
              />

              <b-form-group
                :label="$t('fields.phone')"
                label-for="register-username"
                class="auth-label"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Phone"
                  rules="required"
                >
                  <ok-phone-number-input
                    ref="phone"
                    v-model="phone"
                    fetch-country
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <ok-text-input
                v-model="userEmail"
                :label="$t('fields.email')"
                dir="ltr"
                input-class="text-left"
                rules="email"
                :is-required="true"
              />

              <!-- password -->
              <b-form-group
                label-for="register-password"
                :label="$t('fields.password')"
                class="auth-label"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  rules="required"
                >
                  <password-input
                    v-model="password"
                    :label="$t('fields.password')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-button
                variant="primary"
                block
                type="submit"
                :disabled="submitting"
                @click.prevent="register"
              >
                {{ $t('auth.signup') }}
                <b-spinner
                  v-if="submitting"
                  small
                  class="mr-50"
                />
              </b-button>
            </b-form>
          </validation-observer>

          <b-card-text class="text-center mt-2">
            <span> {{ $t('auth.have-account') }}</span>
            <b-link :to="{name:'login', query: $route.query}">
              <span> {{ $t('auth.signin-instead') }}</span>
            </b-link>
          </b-card-text>

          <div class="divider my-2">
            <div class="divider-text">
              {{ $t('general.or') }}
            </div>
          </div>

          <!-- social buttons -->
          <social-auth
            v-if="socialAuth"
            type="register"
            @error="error = $event"
            @clear-error="error = null"
          />
        </template>
      </b-card>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BLink, BButton, BForm, BFormGroup, BCardTitle, BCardText, BCard, BSpinner, BAlert,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import pick from 'lodash/pick'
import OkPhoneNumberInput from '@/@okmenu/components/inputs/OkPhoneNumberInput'
import OkTextInput from '@/@okmenu/components/inputs/OkTextInput'
import PasswordInput from '@/@okmenu/components/inputs/PasswordInput'
import OkLogo from '@/@okmenu/icons/OkLogo'
import SocialAuth from '@/views/auth/social-auth'
import ErrorAlert from '@/@okmenu/components/alerts/ErrorAlert'
import MarketingService, { MARKETING_EVENTS } from '@/services/marketing-service'

export default {
  components: {
    BAlert,
    ErrorAlert,
    SocialAuth,
    OkLogo,
    PasswordInput,
    OkTextInput,
    OkPhoneNumberInput,
    BCard,
    BLink,
    BButton,
    BForm,
    BCardText,
    BCardTitle,
    BFormGroup,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      name: '',
      phone: '',
      userEmail: '',
      password: '',
      submitting: false,
      error: null,
      required,
      email,
      fieldsChanged: false,
      message: '',
      socialAuth: false,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    form() {
      return {
        email: this.userEmail,
        password: this.password,
        business_phone: this.phone,
        name: this.name,
      }
    },
  },
  watch: {
    form() {
      if (this.fieldsChanged) return
      MarketingService.triggerEvent(MARKETING_EVENTS.REGISTER_BEGIN, 'auth', 'user start registering')
      this.fieldsChanged = true
    },
  },
  methods: {
    async register() {
      const isValid = await this.$refs.registerForm.validate() && this.$refs.phone.validate()

      if (!isValid) {
        this.error = this.$tc('errors.field-invalid', 2)
        return
      }

      const { country, country_iso } = this.$refs.phone.getCountryDetails()
      const credentials = {
        email: this.userEmail.toLowerCase(),
        password: this.password,
        business_phone: this.phone,
        country,
        country_iso,
        name: this.name,
      }

      this.error = null
      this.submitting = true
      this.message = ''
      this.$store.dispatch('auth/REGISTER', credentials)
        .then(user => {
        //  const pendingRoute = StorageService.getItem('okmenu_route_after_auth', null, -1)
          MarketingService.triggerEvent(MARKETING_EVENTS.REGISTER_COMPLETE, 'auth', 'user registered', pick(user, ['id', 'email', 'name', 'restaurant_name', 'currency', 'phone']))
          // this.$router.push(pendingRoute ?? { name: 'admin:dashboard' }).catch(() => {})
          this.message = this.$t('auth.register-request-msg')
        })
        .catch(err => {
          this.$notifyError(err)
          this.error = err
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
  metaInfo() {
    return {
      title: this.$t('auth.signup'),
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
